import { useEffect, useState, useCallback, useMemo, useContext } from "react";
import { LoadingDots } from "../../../components/general";
import { SearchComponent } from "../../../components/components/js/SearchComponent";
import { TableComponent } from "../../../components/components/js/TableComponent";
import { HeaderComponent } from "../../../components/components/js/HeaderComponent";
import AddRoomModal from "../../../components/components/js/RoomModal";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import { downloadCSV, getFilters } from "../../../utils/exportUtils";
import AuthContext from "../../../context/AuthProvider";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ADMIN_ROOMS_ROUTE } from "../../../routes/RoutesPathes";

const COLUMNS = ["room", "capacity", "students", "actions"];

export const Rooms = () => {
  const axiosPrivate = useAxiosPrivate();

  const [rooms, setRooms] = useState([]);
  const navigate = useNavigate();
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const { school } = useContext(AuthContext);
  const { t } = useTranslation();

  const filters = useMemo(() => getFilters(COLUMNS, t), [t]);

  const fetchRooms = useCallback(async () => {
  try {
    setLoading(true);
    setError(null);
    const response = await axiosPrivate.get(
      API_URLS.getSchoolRooms(school.id, true)
    );

    const roomsData = Array.isArray(response.data)
      ? response.data
      : response.data?.results ?? [];

    // The classrooms endpoint doesn't return student counts, so fetch each
    // room's students separately and count them in parallel.
    const studentCounts = await Promise.all(
      roomsData.map(async (room) => {
        try {
          const studentsRes = await axiosPrivate.get(
            API_URLS.getStudentsByRoom(school.id, room.id)
          );
          const studentsList = Array.isArray(studentsRes.data)
            ? studentsRes.data
            : studentsRes.data?.results ?? [];
          return (
            studentsRes.data?.total_items ??
            studentsRes.data?.count ??
            studentsList.length
          );
        } catch (err) {
          console.error(`Failed to fetch students for room ${room.id}:`, err);
          return 0;
        }
      })
    );

    const formattedData = roomsData.map((room, index) => ({
      id: room.id,
      image: room.image || "",
      room: room.name,
      capacity: room.capacity,
      students: studentCounts[index],
    }));

    setRooms(formattedData);
    setFilteredRooms(formattedData);
  } catch (error) {
    console.error("GET request error:", error);
    setError("Failed to load rooms. Please try again later.");
    setRooms([]);
  } finally {
    setLoading(false);
  }
}, [axiosPrivate, school.id]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const handleCloseAddModal = () => setShowAddModal(false);
  const handleOpenAddModal = () => setShowAddModal(true);

  const handleNavigate = (row) => {
    navigate(`/admin/rooms/${row.id}/students?room=${encodeURIComponent(row.room)}`, {
      state: {
        className: row.room,
      },
    });
  };

  const handleExport = () => {
    const headers = COLUMNS.filter((col) => col.key !== "actions").map(
      (column) => column.label
    );
    const data = rooms.map((room) => [room.room, room.capacity, room.students]);
    downloadCSV(data, headers, "Rooms");
  };

  const handleSearch = (value, filter) => {
    if (!value || !filter) return setFilteredRooms(rooms);

    const query = value.toLowerCase();
    const results = rooms.filter((s) => {
      return s[filter].toString().toLowerCase().includes(query);
    });
    setFilteredRooms(results);
  };

  const handleDelete = async (row) => {
    if (!row) return;

    try {
      setLoading(true);
      await axiosPrivate.delete(API_URLS.getRoomDetail(school.id, row.id));
      fetchRooms();
    } catch (error) {
      console.error("Delete request error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="d-block ms-auto px-4"
      style={{
        minHeight: "70vh",
      }}
    >
      <HeaderComponent
        buttonName="add_room"
        export_fn={handleExport}
        setShow={handleOpenAddModal}
        title="rooms"
      />

      <SearchComponent
        items={filters}
        onSearch={handleSearch}
        placeholder={"room"}
      />

      {loading ? (
        <LoadingDots />
      ) : error ? (
        <div className="d-flex justify-content-center align-items-center vh-100 fw-bold fs-1 text-danger">
          {error}
        </div>
      ) : filteredRooms.length > 0 ? (
        <TableComponent
          columns={COLUMNS}
          data={filteredRooms}
          page="room"
          onEdit={handleNavigate}
          onDelete={handleDelete}
          getRowUrl={(row) => `${ADMIN_ROOMS_ROUTE}/${row.id}/students`}
        />
      ) : (
        <div className="d-flex justify-content-center align-items-center vh-100 fw-bold fs-1">
          {t("noRooms")}
        </div>
      )}

      <AddRoomModal
        open={showAddModal}
        onClose={handleCloseAddModal}
        modalName="add"
      />
    </div>
  );
};